import { useState } from 'react';
import { ABSTAIN } from '@ringmaster/shared';
import type { PrivateView, PublicView, VoteAction } from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { FixedActionBar } from '../ui/FixedActionBar.js';
import { Glyph } from '../ui/Glyph.js';
import { useSequence } from '../fx/sequencer.js';
import { roleAccent } from './roleGlyph.js';
import plaqueClean from '../assets/ceremony/plaque-clean.webp';
import waxSeal from '../assets/ornaments/wax-seal.webp';
import type { RoleId } from '@ringmaster/shared';

/**
 * VoteScreen — the ballot ceremony (living players + host view; the dead see
 * DeadScreen). Candidates are tappable engraved plaques; selecting one slides a
 * wax-seal ballot preview into the action bar; confirming stamps the seal and
 * locks. The countdown lives in the shared GameHeader (server-driven).
 *
 * IMPORTANT — this is a RESKIN of the interaction, not a logic change. The
 * pending/locked state machine and the SUBMIT_VOTE intent are byte-identical to
 * the prior screen: tap sets `pending`, Confirm sends
 * `{ type: 'SUBMIT_VOTE', targetId: pending === ABSTAIN ? ABSTAIN : pending }`
 * and sets `locked`. Nothing about the network contract changed.
 *
 * RESOLVE_VOTE is a staged reveal driven by useSequence('LYNCH_REVEAL' /
 * 'LYNCH_TIE') so phones land each beat in lockstep with the Stage and the
 * narrator (the old hardcoded REVEAL_*_MS constants are gone). Calm near-black
 * throughout — no blood flash (that is reserved for the player who died).
 */

export function VoteScreen({
  privateView,
  publicView,
  actionOffset = 0,
}: {
  privateView: PrivateView;
  publicView: PublicView;
  actionOffset?: number;
}) {
  const { send } = useRoom();
  const action = privateView.action.type === 'VOTE' ? (privateView.action as VoteAction) : null;
  const [pending, setPending] = useState<string | typeof ABSTAIN | null>(action?.myVote ?? null);
  const [locked, setLocked] = useState(action?.myVote != null);
  const [stamping, setStamping] = useState(false);

  const nameMap = Object.fromEntries(publicView.players.map((p) => [p.id, p.name]));
  const candidates = action?.candidates ?? [];

  const isResolve = publicView.phase === 'RESOLVE_VOTE';
  const lynchResult = publicView.lynchResult;
  // One Night has no elimination round to fall back on — the physical game
  // has everyone point at someone. `allowAbstain` stays wired in the engine
  // as the TIMER fallback (a silent player still resolves the game), but the
  // UI must not offer it as a choice for this game.
  const showAbstain = publicView.gameId !== 'onenight';

  function handleConfirm() {
    if (!pending) return;
    // ── Network contract (unchanged): submit the vote intent, then lock. ──
    send({ type: 'SUBMIT_VOTE', targetId: pending === ABSTAIN ? ABSTAIN : pending });
    setLocked(true);
    // Ceremony only — a stamp flourish + a short haptic on the seal press.
    setStamping(true);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(30);
      } catch {
        /* haptics unsupported — silent */
      }
    }
  }

  if (isResolve && lynchResult) {
    return (
      <ResolveVote
        publicView={publicView}
        lynchResult={lynchResult}
        nameMap={nameMap}
      />
    );
  }

  // ── VOTE phase ───────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #080808 0%, #140002 100%)',
        padding: 'var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-5))',
        paddingBottom: locked ? 'var(--space-5)' : 176 + actionOffset,
        gap: 'var(--space-5)',
      }}
    >
      <VoteStyles />

      {/* Header */}
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: '0 0 var(--space-2)', letterSpacing: '0.02em' }}>
          Cast your ballot
        </h1>
        {publicView.voteProgress && (
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 'var(--space-2) 0 0' }}>
            {publicView.voteProgress.voted} / {publicView.voteProgress.total} sealed
          </p>
        )}
      </div>

      {locked ? (
        <SealedBallot pending={pending} nameMap={nameMap} stamping={stamping} />
      ) : (
        <>
          {/* Candidate plaques */}
          <div style={{ flex: 1 }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {candidates.map((id) => {
                const pName = nameMap[id] ?? id;
                const isSelected = pending === id;
                const isMe = id === privateView.playerId;
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => setPending(id)}
                      className={`vote-plaque${isSelected ? ' is-selected' : ''}`}
                      aria-pressed={isSelected}
                    >
                      <span
                        className="vote-radio"
                        aria-hidden
                        style={{
                          borderColor: isSelected ? 'var(--blood-5, #c22f3e)' : 'var(--surface-stroke)',
                          background: isSelected ? 'var(--blood-4, #990000)' : 'transparent',
                        }}
                      />
                      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
                        {pName}
                        {isMe && <span style={{ color: 'var(--text-tertiary)', fontSize: 13, fontWeight: 500 }}>(you)</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Abstain + seal-preview confirm — fixed, always visible */}
          <FixedActionBar offsetBottom={actionOffset}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {showAbstain && (
                <button
                  type="button"
                  onClick={() => setPending(ABSTAIN)}
                  className={`vote-abstain${pending === ABSTAIN ? ' is-selected' : ''}`}
                >
                  <Glyph name="scales" size={18} tone="bone" />
                  <span>Abstain</span>
                </button>
              )}
              <button
                type="button"
                disabled={!pending}
                onClick={handleConfirm}
                className="vote-confirm"
                style={{
                  background: pending ? 'var(--btn-primary-bg)' : 'rgba(255,255,255,0.1)',
                  color: pending ? 'var(--btn-primary-text)' : 'var(--text-tertiary)',
                  cursor: pending ? 'pointer' : 'not-allowed',
                  boxShadow: pending ? 'var(--rim-gold, 0 0 24px rgba(230,184,102,0.5))' : 'none',
                }}
              >
                {/* Selecting a target slides a wax-seal ballot preview into the bar. */}
                {pending && pending !== ABSTAIN && (
                  <img src={waxSeal} alt="" aria-hidden className="vote-confirm-seal" />
                )}
                <span>
                  {pending
                    ? pending === ABSTAIN
                      ? 'Seal: Abstain'
                      : `Seal your vote: ${nameMap[pending] ?? pending}`
                    : 'Choose someone'}
                </span>
              </button>
            </div>
          </FixedActionBar>
        </>
      )}
    </div>
  );
}

// ── Sealed (locked) state ──────────────────────────────────────────────────────

function SealedBallot({
  pending,
  nameMap,
  stamping,
}: {
  pending: string | typeof ABSTAIN | null;
  nameMap: Record<string, string>;
  stamping: boolean;
}) {
  const label =
    pending === ABSTAIN
      ? 'You abstained'
      : pending
        ? `You sealed your vote for ${nameMap[pending] ?? pending}`
        : 'Your ballot is sealed';
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="vote-sealed">
        <div className={`vote-sealed-seal${stamping ? ' is-stamping' : ''}`}>
          <img src={waxSeal} alt="" aria-hidden style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: 20, margin: '0 0 var(--space-2)', letterSpacing: '0.03em' }}>
          Your ballot is sealed
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 15, margin: 0 }}>{label}</p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 13, margin: 'var(--space-3) 0 0' }}>
          Awaiting the other ballots&hellip;
        </p>
      </div>
    </div>
  );
}

// ── RESOLVE_VOTE — sequenced reveal ────────────────────────────────────────────

function ResolveVote({
  publicView,
  lynchResult,
  nameMap,
}: {
  publicView: PublicView;
  lynchResult: NonNullable<PublicView['lynchResult']>;
  nameMap: Record<string, string>;
}) {
  const hasLynch = !!lynchResult.targetId;
  const isTie = !!lynchResult.tie;
  // Drive beats from the room-wide tuning so phones land in step with the stage.
  const seq = useSequence(isTie || !hasLynch ? 'LYNCH_TIE' : 'LYNCH_REVEAL', { enabled: true });
  const showName = seq.has('name') || seq.has('verdict');
  const showTally = seq.has('tally');

  const lynched = lynchResult.targetId
    ? publicView.players.find((p) => p.id === lynchResult.targetId)
    : null;
  const revealedRole = lynched?.revealedRole as RoleId | undefined;

  // Suspense beat (before name/verdict): noose glyph + a slow vignette pulse.
  if (!showName) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #080808 0%, #0d0507 100%)',
          gap: 'var(--space-5)',
          padding: 'var(--space-5)',
          overflow: 'hidden',
        }}
      >
        <VoteStyles />
        {/* Vignette pulse — the tightening noose of suspense. */}
        <div aria-hidden className="vote-suspense-vignette" />
        <div className="vote-suspense-glyph">
          <Glyph name="noose" size={64} tone="bone" />
        </div>
        <p
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            color: 'rgba(255,255,255,0.85)',
            margin: 0,
            textAlign: 'center',
            letterSpacing: '0.04em',
          }}
        >
          The village has spoken&hellip;
        </p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 14, margin: 0, textAlign: 'center' }}>
          Listen to the Ringmaster.
        </p>
      </div>
    );
  }

  const accent = revealedRole ? roleAccent(revealedRole) : 'var(--gold-5, #e6b866)';

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #080808 0%, #0d0507 100%)',
        padding: 'var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-5))',
        paddingBottom: 'max(var(--space-5), env(safe-area-inset-bottom, 0px) + var(--space-3))',
        gap: 'var(--space-5)',
      }}
    >
      <VoteStyles />

      <div style={{ textAlign: 'center' }} className="vote-verdict-appear">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
          <Glyph name={isTie ? 'scales' : 'gallows'} size={44} tone={isTie ? 'bone' : 'blood'} />
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: '0 0 var(--space-2)', letterSpacing: '0.02em' }}>
          {isTie ? 'The village could not agree' : lynched ? `${lynched.name} is condemned` : 'No one was lynched'}
        </h1>
        {!isTie && revealedRole && (
          <p style={{ color: accent, fontSize: 16, margin: 0, fontWeight: 600 }}>
            They were a {revealedRole.charAt(0) + revealedRole.slice(1).toLowerCase()}
          </p>
        )}
      </div>

      {/* Engraved tally — the last beat to land */}
      {showTally && (
        <div className="vote-tally">
          <div className="vote-tally-head">How everyone voted</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {Object.entries(lynchResult.votes).map(([voterId, targetId]) => {
              const voterName = nameMap[voterId] ?? voterId;
              const isAbstain = targetId === ABSTAIN;
              const targetName = isAbstain ? 'Abstain' : (nameMap[targetId] ?? targetId);
              const isCondemned = targetId === lynchResult.targetId;
              return (
                <div key={voterId} className="vote-tally-row">
                  <span>{voterName}</span>
                  <span
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      color: isAbstain
                        ? 'var(--text-tertiary)'
                        : isCondemned
                          ? 'var(--blood-6, #ff5a4f)'
                          : 'rgba(255,255,255,0.6)',
                    }}
                  >
                    {isAbstain ? '— abstain' : `→ ${targetName}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Scoped styles ──────────────────────────────────────────────────────────────

function VoteStyles() {
  return (
    <style>{`
      .vote-plaque {
        width: 100%; min-height: 56px; text-align: left;
        padding: 15px var(--space-4);
        border-radius: var(--radius-md);
        border: 1px solid var(--surface-stroke);
        background: linear-gradient(180deg, var(--ink-3, #241619) 0%, var(--ink-2, #150d0f) 100%);
        color: var(--text-primary);
        cursor: pointer; font-family: var(--font-ui); font-size: 16px;
        display: flex; align-items: center; gap: var(--space-3);
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.05);
        transition: border-color var(--duration-fast) var(--ease-standard), background var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-standard);
      }
      .vote-plaque.is-selected {
        border-color: var(--blood-5, #c22f3e);
        background: linear-gradient(180deg, rgba(153,0,0,0.28) 0%, rgba(50,0,1,0.4) 100%);
        box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), var(--rim-blood, 0 0 24px rgba(153,0,0,0.55));
      }
      .vote-plaque:active { transform: scale(0.99); }
      @media (hover: hover) and (pointer: fine) {
        .vote-plaque:hover:not(.is-selected) { border-color: rgba(194,47,62,0.55); background: linear-gradient(180deg, #2e1d20 0%, #1c1214 100%); transform: translateY(-1px); }
        .vote-abstain:hover:not(.is-selected) { border-color: rgba(255,255,255,0.35); background: rgba(255,255,255,0.05); }
        .vote-confirm:hover:not(:disabled) { filter: brightness(0.9); transform: scale(1.02); }
      }
      .vote-radio {
        width: 20px; height: 20px; border-radius: 50%;
        border: 2px solid var(--surface-stroke);
        display: inline-block; flex-shrink: 0;
        transition: all var(--duration-fast) var(--ease-standard);
      }
      .vote-abstain {
        width: 100%; min-height: 46px; padding: 10px var(--space-4);
        border-radius: var(--radius-md);
        border: 1px solid var(--surface-stroke);
        background: var(--ink-2, #150d0f);
        color: var(--text-secondary); cursor: pointer;
        font-family: var(--font-ui); font-size: 15px;
        display: flex; align-items: center; justify-content: center; gap: var(--space-2);
        transition: all var(--duration-fast) var(--ease-standard);
      }
      .vote-abstain.is-selected {
        border-color: rgba(255,255,255,0.5);
        background: rgba(255,255,255,0.08);
        color: var(--text-primary);
      }
      .vote-confirm {
        width: 100%; font-family: var(--font-ui); font-weight: 600; font-size: 16px;
        border-radius: var(--radius-pill); padding: 16px 24px; border: none;
        min-height: 52px; position: relative; overflow: hidden;
        display: flex; align-items: center; justify-content: center; gap: 10px;
        transition: background var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard), filter var(--duration-fast) var(--ease-standard), transform var(--duration-fast) var(--ease-standard);
      }
      .vote-confirm-seal { width: 26px; height: 26px; object-fit: contain; animation: vote-seal-slide 0.32s var(--ease-spring); }
      @keyframes vote-seal-slide { from { opacity: 0; transform: translateX(-12px) scale(0.6); } to { opacity: 1; transform: none; } }

      .vote-sealed { width: 100%; text-align: center; padding: var(--space-6) var(--space-5);
        background:
          linear-gradient(180deg, rgba(21,13,15,0.86) 0%, rgba(11,7,9,0.92) 100%),
          url(${plaqueClean}) center/cover;
        border: 1px solid var(--surface-stroke); border-radius: var(--radius-card);
        box-shadow: var(--shadow-card); }
      .vote-sealed-seal { width: 84px; height: 84px; margin: 0 auto var(--space-4);
        filter: drop-shadow(0 6px 14px rgba(0,0,0,0.6)); }
      .vote-sealed-seal.is-stamping { animation: vote-stamp 0.42s var(--ease-dramatic); }
      @keyframes vote-stamp {
        0% { transform: scale(1.9) rotate(-8deg); opacity: 0; }
        55% { transform: scale(0.92) rotate(2deg); opacity: 1; }
        100% { transform: none; opacity: 1; }
      }

      .vote-suspense-vignette { position: absolute; inset: 0; pointer-events: none;
        background: radial-gradient(120% 90% at 50% 45%, transparent 40%, rgba(0,0,0,0.75) 100%);
        animation: vote-vignette-pulse 2.4s ease-in-out infinite; }
      .vote-suspense-glyph { animation: vote-suspense-bob 2.4s ease-in-out infinite; }
      @keyframes vote-vignette-pulse { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
      @keyframes vote-suspense-bob { 0%,100% { transform: translateY(0); opacity: 0.7; } 50% { transform: translateY(-4px); opacity: 1; } }

      .vote-verdict-appear { animation: vote-verdict-in 0.6s var(--ease-standard); }
      @keyframes vote-verdict-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }

      .vote-tally { background: linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.35) 100%);
        border: 1px solid var(--surface-stroke); border-radius: var(--radius-card);
        padding: var(--space-5); animation: vote-verdict-in 0.5s var(--ease-standard); }
      .vote-tally-head { color: var(--gold-5, #e6b866); font-size: 12px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: var(--space-4); }
      .vote-tally-row { display: flex; justify-content: space-between; font-size: 14px;
        color: rgba(255,255,255,0.82); padding: 8px 0; border-bottom: 1px solid rgba(255,255,255,0.07); }
      .vote-tally-row:last-child { border-bottom: none; }

      @media (prefers-reduced-motion: reduce) {
        .vote-confirm-seal, .vote-sealed-seal.is-stamping, .vote-suspense-vignette,
        .vote-suspense-glyph, .vote-verdict-appear, .vote-tally { animation: none; }
        .vote-suspense-vignette { opacity: 0.8; }
      }
    `}</style>
  );
}
